import { Injectable } from '@angular/core';
import { Member } from '../../shared/interface/member.interface';
import { MemberTypeId } from '../constants/member-type-id.enum';
import { AppCountryConfig } from '../interface/app-config';

@Injectable()
export class AppConfigService {
    constructor(
    ) { }

    /**
    * @description this method will get the config settings for combo packs page
    * @date 2019-09-16
    * @memberof ProductDetailConfigSettings
    */
    public getSettings(countryCode: string, userInfo: Member): AppCountryConfig {
        let appCountryConfig: AppCountryConfig;
        switch (countryCode.toUpperCase()) {
            case 'USA':
                appCountryConfig = this.loadUSASettings(userInfo);
                break;
            case 'CAN':
                appCountryConfig = this.loadCANSettings(userInfo);
                break;
            case 'GBR':
                appCountryConfig = this.loadGBRSettings(userInfo);
                break;
            case 'ARE':
                appCountryConfig = this.loadARESettings(userInfo);
                break;
            case 'SAU':
                appCountryConfig = this.loadSAUSettings(userInfo);
                break;
            case 'QAT':
                appCountryConfig = this.loadQATSettings(userInfo);
                break;
            case 'IRL':
                appCountryConfig = this.loadIRLSettings(userInfo);
                break;
            case 'ILN':
                appCountryConfig = this.loadILNSettings(userInfo);
                break;
            case 'KWT':
                appCountryConfig = this.loadKWTSettings(userInfo);
                break;
            case 'BHR':
                appCountryConfig = this.loadBHRSettings(userInfo);
                break;
            case 'OMN':
                appCountryConfig = this.loadOMNSettings(userInfo);
                break;
            case 'AUS':
                appCountryConfig = this.loadAUSSettings(userInfo);
                break;
            case 'FIN':
                appCountryConfig = this.loadFINSettings(userInfo);
                break;
            case 'NOR':
                appCountryConfig = this.loadNORSettings(userInfo);
                break;
            case 'SWE':
                appCountryConfig = this.loadSWESettings(userInfo);
                break;
            case 'DNK':
                appCountryConfig = this.loadDNKSettings(userInfo);
                break;
            case 'BEL':
                appCountryConfig = this.loadBELSettings(userInfo);
                break;
            case 'LUX':
                appCountryConfig = this.loadLUXSettings(userInfo);
                break;
            case 'NLD':
                appCountryConfig = this.loadNLDSettings(userInfo);
                break;  
            case 'NZL':
                appCountryConfig = this.loadNZLSettings(userInfo);
                break;  
            case 'POL':
                appCountryConfig = this.loadPOLSettings(userInfo);
                break; 
            case 'DEU':
                appCountryConfig = this.loadDEUSettings(userInfo);
                break;
            case 'AUT':
                appCountryConfig = this.loadAUTSettings(userInfo);
                break;  
            case 'BGR':
                appCountryConfig = this.loadBGRSettings(userInfo);
                break;   
            case 'MKD':
                appCountryConfig = this.loadMKDSettings(userInfo);
                break; 
            case 'CHE':
                appCountryConfig = this.loadCHESettings(userInfo);
                break;
            case 'ZAF':
                appCountryConfig = this.loadZAFSettings(userInfo);
                break;
            case 'NGA':
                appCountryConfig = this.loadNGASettings(userInfo);
                break;
            case 'SEN':
                appCountryConfig = this.loadSENSettings(userInfo);
                break;   
            case 'ISL':
                appCountryConfig = this.loadISLSettings(userInfo);
                break;  
            case 'TZA':
                appCountryConfig = this.loadTZASettings(userInfo);
                break; 
            case 'CIV':
                appCountryConfig = this.loadCIVSettings(userInfo);
                break; 
            case 'COD':
                appCountryConfig = this.loadCODSettings(userInfo);
                break;
            case 'GHA':
                appCountryConfig = this.loadGHASettings(userInfo);
                break;
            case 'COG':
                appCountryConfig = this.loadCOGSettings(userInfo);
                break; 
            default:
                appCountryConfig = this.loadUSASettings(userInfo);
                break;
        }
        return appCountryConfig;
    }

    /**
     * @description this method will return usa settings
     * @date 2019-09-19
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadUSASettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'USA',
            displayVat: false,
            displayTax: true,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: true,
            ignoreCompareTax: false,
            skipShippingTaxCalculationInShipping: true,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    /**
     * @description this method will return CAN settings
     * @date 2019-09-19
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadCANSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'CAN',
            displayVat: false,
            displayTax: false,
            displayGST: true,
            displayPST: true,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            displayParticipatingCountry: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: true,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false

        };
        return appConfigSetting;
    }

    /**
     * @description this method will return GBR settings
     * @date 2019-09-19
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadGBRSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'GBR',
            displayVat: userInfo && userInfo.vatRegistered == true  && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: true,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: true,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: true,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: true,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: true,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }


     /**
     * @description this method will return ARE settings
     * @date 2020-05-11
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadARESettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'ARE',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false,
            displayTaxInvoiceTitle:true,
        };
        return appConfigSetting;
    }

      /**
     * @description this method will return SAU settings
     * @date 2020-06-23
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadSAUSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'SAU',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }
    /**
     * @description returns QAT app settings
     * @author njagadeesan
     * @date 2020-11-24
     * @private
     * @param {Member} userInfo
     * @returns {AppCountryConfig}
     * @memberof AppConfigService
     */
    private loadQATSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'QAT',
            displayVat: false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

      /**
     * @description this method will return GBR settings
     * @date 2019-09-19
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadIRLSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'IRL',
            displayVat: userInfo && userInfo.vatRegistered == true  && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: true,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: true,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: true,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            skipShippingTaxCalculationInShipping: true,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

      /**
     * @description this method will return GBR settings
     * @date 2019-09-19
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadILNSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'ILN',
            displayVat: userInfo && userInfo.vatRegistered == true  && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: true,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: true,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: true,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: true,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: true,
            skipShippingTaxCalculationInShipping: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }


    /**
     * @description this method will return KWT session 
     * @author Akhil
     * @date 2020-12-16
     * @private
     * @param {Member} userInfo
     * @returns {AppCountryConfig}
     * @memberof AppConfigService
     */
    private loadKWTSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'KWT',
            displayVat: false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    /**
* @description this method will return ZAF settings
* @date 2023-03-21
* @private
* @returns {AppConfigSetting}
* @memberof AppConfigService
*/
    private loadZAFSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'ZAF',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            enableDeliveryInstructions: false,
            freeShippingFPCOrder: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: true,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    /**
     * @description this method will return NGA settings
     * @date 2023-03-21
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadNGASettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'NGA',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    /**
     * @description this method will return BHR settings
     * @date 2021-02-11
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadBHRSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'BHR',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

     /**
     * @description this method will return OMN settings
     * @date 2021-03-08
     * @private
     * @returns {AppConfigSetting}
     * @memberof AppConfigService
     */
    private loadOMNSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'OMN',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    /**
     * @description this method will return AUS settings 
     * @author Akhil
     * @date 2021-03-15
     * @private
     * @param {Member} userInfo
     * @returns {AppCountryConfig}
     * @memberof AppConfigService
     */
    private loadAUSSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'AUS',
            displayVat: false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            displayParticipatingCountry: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: true,
            hideGovernmentidFPCToFBOUpgrade: true,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            skipShippingTaxCalculationInShipping: true,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice :true,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    private loadFINSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'FIN',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            hideGovernmentidFPCToFBOUpgrade : true,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : true
        };
        return appConfigSetting;
    }

    private loadNORSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'NOR',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            enableAccountNumberFormat : true,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : true,
            hideGovernmentidFPCToFBOUpgrade: true,
        };
        return appConfigSetting;
    }

    private loadDNKSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'DNK',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : true,
            hideGovernmentidFPCToFBOUpgrade: true,
        };
        return appConfigSetting;
    }

    private loadSWESettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'SWE',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : true,
            hideGovernmentidFPCToFBOUpgrade: true,
        };
        return appConfigSetting;
    }
    private loadBELSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'BEL',
            displayVat: userInfo && !userInfo.vatRegistered && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: true,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            enableVatItemizationForNonVat:true,
            hideGovernmentidFPCToFBOUpgrade: true,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            participatingCountryForSponsorSerach: false,
            enableVatExempt : true,
            ignoreCompareTax: true,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }
    private loadLUXSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'LUX',
            displayVat: userInfo && !userInfo.vatRegistered && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: true,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            enableVatItemizationForNonVat:true,
            hideGovernmentidFPCToFBOUpgrade: true,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            participatingCountryForSponsorSerach: false,
            enableVatExempt : true,
            ignoreCompareTax: true,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false

        };
        return appConfigSetting;
    }
    private loadNLDSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'NLD',
            displayVat: userInfo && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: true,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            enableVatItemizationForNonVat:true,
            hideGovernmentidFPCToFBOUpgrade: true,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            calculateVatForVatRegisterUser:true,
            participatingCountryForSponsorSerach: false,
            enableVatExempt : true,
            ignoreCompareTax: true,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }
    
    /**
     * @description this method will return NZL settings 
     * @date 2021-04-06
     * @private
     * @param {Member} userInfo
     * @returns {AppCountryConfig}
     * @memberof AppConfigService
     */
     private loadNZLSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'NZL',
            displayVat: false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            displayCustomTaxName: true,
            hideGovernmentidFPCToFBOUpgrade: true,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            skipShippingTaxCalculationInShipping: true,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : true,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    /**
     * @description this method Will return POL settings
     * @author Akhil
     * @date 2021-05-18
     * @private
     * @param {Member} userInfo
     * @returns {AppCountryConfig}
     * @memberof AppConfigService
     */
    private loadPOLSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'POL',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: false,
            enableValidateProductTypes: true,
            businessEntityConfig: {
                displayVATID: false,
                hideStateCode: true,
                showVatPayer: true,
                removesrtDateValidation: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            hideGovernmentidFPCToFBOUpgrade: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }
    private loadDEUSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'DEU',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true,
                businessEntityIDOptional: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: true,
            showBonusInfoForFPC : true,
            hideGovernmentidFPCToFBOUpgrade: true,
            bondedWarehouseProject: true,
            enablePrivacyPreference: true
        };
        return appConfigSetting;
    }
    private loadAUTSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'AUT',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true,
                businessEntityIDOptional: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: true,
            hideGovernmentidFPCToFBOUpgrade: true,
            showBonusInfoForFPC: true,
            enablePrivacyPreference: true
        };
        return appConfigSetting;
    }
    private loadBGRSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'BGR',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false,
            isWireTransferLabelWithoutGmbh: true
        };
        return appConfigSetting;
    }
    private loadMKDSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'MKD',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: true,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            showBonusInfoForFPC : false,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false,
            isWireTransferLabelWithoutGmbh: true
        };
        return appConfigSetting;
    }
    private loadCHESettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'CHE',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: false,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true,
                businessEntityIDOptional: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: true,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: true,
            hideGovernmentidFPCToFBOUpgrade: true,
            showBonusInfoForFPC: true,
            enablePrivacyPreference: true,
            displayMinValidation: true
        };
        return appConfigSetting;
    }

    private loadSENSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'SEN',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: true,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification:true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            businessEntityConfig: {
                displayVATID: true,
                hideStateCode: true,
                businessEntityIDOptional: true
            },
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice : false,
            isBSNEnabled: true,
            disableRetailOrderRules : false,
            displayTaxInclusivePrice: false,
            showBonusInfoForFPC : true,
            hideGovernmentidFPCToFBOUpgrade: true,
            bondedWarehouseProject: true,
            removeCancelAndRefund:true,
            displayStatementForWavePayment:true,
            enablePaymentReferenceNumber:true,
            freeProductNameChange: true,
            roundoffTaxAmount: true
        };
        return appConfigSetting;
    }
    private loadISLSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'ISL',
            displayVat: userInfo && userInfo.vatRegistered == true && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: true,
            displayHandling: false,
            displayPurchaseLimitationMessage: true,
            displayDistributorRecognition: true,
            displayShippingTooltip: true,
            displayDeleteAllCartItems: false,
            skipAddressVerification: false,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: true,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: true,
            freeShippingFPCOrder: userInfo && userInfo.memberTypeId === MemberTypeId.PREFERREDCUSTOMER ? true : false,
            calculateNetUnitPrice: userInfo && !userInfo.vatRegistered && userInfo.memberTypeId === MemberTypeId.DISTRIBUTOR ? true : false,
            enableDeliveryInstructions: true,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayOnlyEnrollmentItem: false,
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            skipShippingTaxCalculationInShipping: true,
            calculateShippingWithTax: true,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: true,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false
        };
        return appConfigSetting;
    }

    private loadTZASettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'TZA',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: true,
            disableBusinessEntityForFbo: false,
            enableDeliveryInstructions: false,
            freeShippingFPCOrder: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: true,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    private loadCIVSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'CIV',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: false,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: false,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            enableDeliveryInstructions: false,
            freeShippingFPCOrder: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: true,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: true,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    private loadCODSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'COD',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    private loadGHASettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'GHA',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }

    private loadCOGSettings(userInfo: Member): AppCountryConfig {
        const appConfigSetting: AppCountryConfig = {
            countryCode: 'COG',
            displayVat: true,
            displayTax: false,
            displayGST: false,
            displayPST: false,
            fpc5PercentEnabled: true,
            restrictOrder: false,
            displayHandling: false,
            displayPurchaseLimitationMessage: false,
            displayDistributorRecognition: false,
            displayShippingTooltip: false,
            displayDeleteAllCartItems: true,
            skipAddressVerification: true,
            displayIDontHaveSponsorGuestSection: true,
            enableVATCalculation: false,
            fpcToFboUpgradeFlowEnable: false,
            disableBusinessEntityForFbo: false,
            freeShippingFPCOrder: false,
            enableDeliveryInstructions: false,
            orderMinMaxPriceValidation: false,
            hideFPCEnrollmentItem: false,
            displayParticipatingCountry: false,
            displayOnlyEnrollmentItem: false,
            dateFormat: 'DD-MMM-YYYY',
            displayTaxCode: true,
            enableValidateProductTypes: false,
            displayCustomTaxName: false,
            validateUniqueCheckOnLoad: false,
            useAnonymousRetailFboId: false,
            ignoreCompareTax: false,
            calculateShippingWithTax: false,
            calculateMinMaxUsingTaxInclusivePrice: false,
            isBSNEnabled: false,
            showBonusInfoForFPC: false,
            disableRetailOrderRules: false,
            displayTaxInclusivePrice: false,
            removeCancelAndRefund: true
        };
        return appConfigSetting;
    }



}


// WEBPACK FOOTER //
// ./src/app/customerservice/services/app-config.service.ts